import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { toast } from "@/lib/toast";
import {
  createOvertimePayout,
  deleteOvertimePayout,
  formatHours,
  listOvertimePayouts,
  type EmployeeOvertimePayout,
} from "@/lib/employeeTimes";

interface EmployeeOvertimePayoutEditorProps {
  userLogin: string;
  userDisplay: string;
  refreshKey?: number;
  onChanged?: () => void;
}

interface PayoutDraft {
  payment_date: string;
  hours_decimal: string;
  note: string;
}

function formatDateDe(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split("-");
  if (!y || !m || !d) return iso;
  return `${d}.${m}.${y}`;
}

function emptyDraft(): PayoutDraft {
  return {
    payment_date: new Date().toISOString().slice(0, 10),
    hours_decimal: "",
    note: "",
  };
}

export function EmployeeOvertimePayoutEditor({
  userLogin,
  userDisplay,
  refreshKey = 0,
  onChanged,
}: EmployeeOvertimePayoutEditorProps) {
  const [rows, setRows] = useState<EmployeeOvertimePayout[]>([]);
  const [draft, setDraft] = useState<PayoutDraft>(emptyDraft);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listOvertimePayouts({ user_login: userLogin });
      setRows(data);
    } catch (err) {
      toast.error(
        err instanceof ApiError
          ? err.message
          : "Ausbezahlte Überstunden konnten nicht geladen werden.",
      );
    } finally {
      setLoading(false);
    }
  }, [userLogin]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const handleAdd = async () => {
    if (!draft.payment_date) {
      toast.error("Bitte ein Auszahlungsdatum angeben.");
      return;
    }
    const hours = Number(draft.hours_decimal.replace(",", "."));
    if (!Number.isFinite(hours) || hours <= 0) {
      toast.error("Bitte gültige Stunden größer 0 eingeben.");
      return;
    }
    setSaving(true);
    try {
      await createOvertimePayout({
        user_login: userLogin,
        user_display: userDisplay,
        payment_date: draft.payment_date,
        hours_decimal: hours,
        note: draft.note.trim() || undefined,
      });
      toast.success("Auszahlung gespeichert.");
      setDraft(emptyDraft());
      onChanged?.();
      await load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    setDeletingId(id);
    try {
      await deleteOvertimePayout(id);
      toast.success("Auszahlung gelöscht.");
      onChanged?.();
      await load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Löschen fehlgeschlagen.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-4 rounded-ios border border-separator bg-card p-4 shadow-ios">
      <div>
        <h2 className="text-lg font-semibold text-label">Ausbezahlte Überstunden</h2>
        <p className="mt-1 text-sm text-label-secondary">
          Auszahlungen mit Datum erfassen. Am Auszahlungstag werden die Stunden vom
          Überstundenstand abgezogen und in der Jahresübersicht angezeigt.
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : (
        <>
          {rows.length > 0 && (
            <div className="overflow-x-auto rounded-lg border border-separator">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-separator bg-fill-tertiary/40 text-left text-label-secondary">
                    <th className="px-3 py-2 font-medium">Datum</th>
                    <th className="px-3 py-2 font-medium">Stunden</th>
                    <th className="px-3 py-2 font-medium">Notiz</th>
                    <th className="px-3 py-2 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id} className="border-b border-separator last:border-0">
                      <td className="px-3 py-2 tabular-nums text-label">
                        {formatDateDe(row.payment_date)}
                      </td>
                      <td className="px-3 py-2 tabular-nums font-medium text-label">
                        {formatHours(row.hours_decimal)}
                      </td>
                      <td className="px-3 py-2 text-label-secondary">{row.note || "—"}</td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          variant="secondary"
                          disabled={deletingId === row.id}
                          onClick={() => void handleDelete(row.id)}
                        >
                          Löschen
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="space-y-1">
              <span className="eh-label">Auszahlungsdatum</span>
              <input
                type="date"
                className="eh-input w-full"
                value={draft.payment_date}
                onChange={(e) =>
                  setDraft((prev) => ({ ...prev, payment_date: e.target.value }))
                }
              />
            </label>
            <label className="space-y-1">
              <span className="eh-label">Stunden</span>
              <input
                type="text"
                inputMode="decimal"
                className="eh-input w-full"
                placeholder="z. B. 10"
                value={draft.hours_decimal}
                onChange={(e) =>
                  setDraft((prev) => ({ ...prev, hours_decimal: e.target.value }))
                }
              />
            </label>
            <label className="space-y-1 md:col-span-2">
              <span className="eh-label">Notiz</span>
              <input
                type="text"
                className="eh-input w-full"
                placeholder="optional"
                value={draft.note}
                onChange={(e) => setDraft((prev) => ({ ...prev, note: e.target.value }))}
              />
            </label>
          </div>
        </>
      )}

      <div className="flex justify-end">
        <Button disabled={loading || saving} onClick={() => void handleAdd()}>
          Auszahlung hinzufügen
        </Button>
      </div>
    </div>
  );
}
